import { useEffect, useRef, useContext } from 'react';
import { LocalContext } from 'context';
import { getDeviceInfo } from 'utils/deviceDetection';

const UPDATE_INTERVAL = 10000; // Update session time every 10 seconds
const VISIT_STORAGE_KEY = 'visit-tracking';
const DEVICE_LOGGED_STORAGE_KEY = 'device-logged';

function usePresence({ firebase, user, sessionId }) {
  const { browserTabIsActive } = useContext(LocalContext);
  const updateIntervalRef = useRef(null);
  const sessionStartTimeRef = useRef(null);
  const totalSessionTimeRef = useRef(0);
  const lastUpdateTimeRef = useRef(0); // Track seconds already sent to DB
  const isOnlineRef = useRef(false);
  const connectionMonitorRef = useRef(null);

  // Calculate current session time
  const getCurrentSessionTime = () => {
    if (!sessionStartTimeRef.current) return totalSessionTimeRef.current; // Return accumulated time when timer is paused
    const now = Date.now();
    const sessionDuration = Math.floor(
      (now - sessionStartTimeRef.current) / 1000
    );
    return totalSessionTimeRef.current + sessionDuration;
  };

  // Check for new visit using sessionStorage (clears on browser close)
  const checkForNewVisit = () => {
    if (!user?.uid || !sessionId) return false;

    const visitKey = `${VISIT_STORAGE_KEY}-${sessionId}-${user.uid}`;
    const hasVisitedThisSession = sessionStorage.getItem(visitKey);

    if (!hasVisitedThisSession) {
      // New visit detected! Mark this browser session
      sessionStorage.setItem(visitKey, 'true');
      return true;
    }

    return false;
  };

  // Set user online
  const setOnline = async () => {
    if (!firebase?.presence || !user?.uid || !sessionId || isOnlineRef.current)
      return;

    try {
      // Check for new visit before setting user online
      const isNewVisit = checkForNewVisit();

      // Load session data from database or start fresh
      let existingTotalVisits = 0;
      try {
        const existingPresence = await firebase.presence.getUserPresence({
          sessionId,
          uid: user.uid
        });

        if (
          existingPresence &&
          existingPresence.totalSessionTime !== undefined
        ) {
          // Use database data for cross-device continuity
          totalSessionTimeRef.current = existingPresence.totalSessionTime;
          existingTotalVisits = existingPresence.totalVisits || 0;
          sessionStartTimeRef.current = Date.now();
        } else {
          // Fresh session - start from scratch
          sessionStartTimeRef.current = Date.now();
          totalSessionTimeRef.current = 0;
          existingTotalVisits = 0;
        }
      } catch (dbError) {
        // If database read fails, start fresh
        sessionStartTimeRef.current = Date.now();
        totalSessionTimeRef.current = 0;
        existingTotalVisits = 0;
      }

      await firebase.presence.setUserOnline({
        sessionId,
        uid: user.uid,
        name: user.name || 'Unknown User',
        isNewVisit,
        existingTotalSessionTime: totalSessionTimeRef.current,
        existingTotalVisits
      });

      isOnlineRef.current = true;

      // Log device session (only once per browser session)
      const deviceLoggedKey = `${DEVICE_LOGGED_STORAGE_KEY}-${sessionId}-${user.uid}`;
      const alreadyLogged = sessionStorage.getItem(deviceLoggedKey);

      if (!alreadyLogged && firebase?.deviceTracking) {
        try {
          const deviceInfo = getDeviceInfo();
          await firebase.deviceTracking.logDeviceSession({
            sessionId,
            uid: user.uid,
            deviceType: deviceInfo.deviceType,
            userAgent: deviceInfo.userAgent,
            screenWidth: deviceInfo.screenWidth,
            screenHeight: deviceInfo.screenHeight,
            hasTouch: deviceInfo.hasTouch
          });
          sessionStorage.setItem(deviceLoggedKey, 'true');
        } catch (deviceError) {
          console.error('Error logging device session:', deviceError);
          // Don't fail the entire setOnline process for device logging errors
        }
      }
    } catch (error) {
      console.error('Error setting user online:', error);
    }
  };

  // Update session time periodically
  const updateSessionTime = async () => {
    if (!firebase?.presence || !user?.uid || !sessionId || !isOnlineRef.current)
      return;

    try {
      // Check database for newer session data from other devices
      const dbPresence = await firebase.presence.getUserPresence({
        sessionId,
        uid: user.uid
      });

      if (dbPresence?.totalSessionTime > totalSessionTimeRef.current) {
        // Database has newer data from another device - use it
        totalSessionTimeRef.current = dbPresence.totalSessionTime;
        lastUpdateTimeRef.current = dbPresence.totalSessionTime;
        sessionStartTimeRef.current = Date.now(); // Reset timer
      }

      const currentSessionTime = getCurrentSessionTime();
      const sessionTimeDelta = currentSessionTime - lastUpdateTimeRef.current;

      await firebase.presence.updateSessionTime({
        sessionId,
        uid: user.uid,
        totalSessionTime: currentSessionTime,
        sessionTimeDelta
      });

      lastUpdateTimeRef.current = currentSessionTime;
      totalSessionTimeRef.current = currentSessionTime;
      sessionStartTimeRef.current = Date.now(); // Reset start time for next calculation
    } catch (error) {
      console.error('Error updating session time:', error);
    }
  };

  // Start session time update interval
  const startUpdateInterval = () => {
    if (updateIntervalRef.current) return;

    updateIntervalRef.current = setInterval(() => {
      if (browserTabIsActive && isOnlineRef.current) {
        updateSessionTime();
      }
    }, UPDATE_INTERVAL);
  };

  // Stop update interval
  const stopUpdateInterval = () => {
    if (updateIntervalRef.current) {
      clearInterval(updateIntervalRef.current);
      updateIntervalRef.current = null;
    }
  };

  // Monitor connection status (optional - for debugging/awareness)
  const startConnectionMonitoring = () => {
    if (!firebase?.presence || connectionMonitorRef.current) return;

    connectionMonitorRef.current = firebase.presence.monitorConnection(() => {
      // Connection status monitoring for debugging if needed
    });
  };

  // Main presence management effect
  useEffect(() => {
    if (!firebase?.presence || !user?.uid || !sessionId) return;

    const initializePresence = async () => {
      if (browserTabIsActive) {
        await setOnline();
        startUpdateInterval();
        startConnectionMonitoring();
      }
    };

    initializePresence();

    // Cleanup on unmount or dependencies change
    return () => {
      stopUpdateInterval();
      if (connectionMonitorRef.current) {
        connectionMonitorRef.current(); // Unsubscribe from connection monitoring
        connectionMonitorRef.current = null;
      }
      // onDisconnect will handle offline status automatically
    };
  }, [firebase?.presence, user?.uid, sessionId, browserTabIsActive]);

  // Handle browser tab activity changes (preserve session data)
  useEffect(() => {
    if (!firebase?.presence || !user?.uid || !sessionId) return;

    const handleTabActivityChange = async () => {
      if (!browserTabIsActive) {
        // Tab is inactive - accumulate active time and update database
        if (sessionStartTimeRef.current) {
          const activeSessionTime = Math.floor(
            (Date.now() - sessionStartTimeRef.current) / 1000
          );
          totalSessionTimeRef.current += activeSessionTime;
          sessionStartTimeRef.current = null; // Pause timer

          // Update database immediately for cross-device continuity
          try {
            const sessionTimeDelta =
              totalSessionTimeRef.current - lastUpdateTimeRef.current;
            await firebase.presence.updateSessionTime({
              sessionId,
              uid: user.uid,
              totalSessionTime: totalSessionTimeRef.current,
              sessionTimeDelta
            });
          } catch (error) {
            console.error('Error updating session time to database:', error);
          }
        }
        stopUpdateInterval();
      } else {
        // Tab is active - reload latest session data, sync to database, and resume timer
        try {
          const existingPresence = await firebase.presence.getUserPresence({
            sessionId,
            uid: user.uid
          });

          if (existingPresence?.totalSessionTime !== undefined) {
            const isNewVisit = checkForNewVisit();
            totalSessionTimeRef.current = existingPresence.totalSessionTime;

            // Sync fresh data to database to ensure consistency
            await firebase.presence.setUserOnline({
              sessionId,
              uid: user.uid,
              name: user.name || 'Unknown User',
              isNewVisit,
              existingTotalSessionTime: existingPresence.totalSessionTime,
              existingTotalVisits: existingPresence.totalVisits || 0
            });
          }
        } catch (error) {
          // Continue with existing value if database read fails
        }

        sessionStartTimeRef.current = Date.now(); // Resume timer
      }
    };

    // Only run if user is already online (avoid initial setup conflicts)
    if (isOnlineRef.current) {
      handleTabActivityChange();
    }
  }, [browserTabIsActive, firebase?.presence, user?.uid, sessionId]);

  // Update session data on page unload (let onDisconnect handle offline status)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isOnlineRef.current) {
        // Accumulate any remaining active time
        if (sessionStartTimeRef.current) {
          const activeSessionTime = Math.floor(
            (Date.now() - sessionStartTimeRef.current) / 1000
          );
          totalSessionTimeRef.current += activeSessionTime;
        }

        // Try to update database (may not complete due to timing constraints)
        const sessionTimeDelta =
          totalSessionTimeRef.current - lastUpdateTimeRef.current;
        firebase.presence
          .updateSessionTime({
            sessionId,
            uid: user.uid,
            totalSessionTime: totalSessionTimeRef.current,
            sessionTimeDelta
          })
          .catch(() => {
            // Silently fail - periodic updates will catch up on next session
          });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user?.uid, sessionId]);

  return {
    getCurrentSessionTime,
    isOnline: isOnlineRef.current
  };
}

export default usePresence;
